/// <amd-module name="Feed/Legacy/Components/Settings/AddUsersOrGroupsPanel" />
import * as React from "react";

import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { Overlay } from "OfficeFabric/Overlay";
import { Panel, PanelType } from "OfficeFabric/Panel";

import { IVssPageContext } from "VSS/Platform/Context";

import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { IIdentity } from "VSSUI/PeoplePicker";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { FeedSettingsComponents } from "Feed/Common/Constants/Constants";
import { FeedPermission, FeedRole } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { IdentityPickerWrapper } from "Feed/Legacy/Components/Settings/IdentityPickerWrapper";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { RoleHelper } from "Feed/Legacy/Helpers/RoleHelper";

export interface IAddUsersOrGroupsPanelProps {
    pageContext: IVssPageContext;
    /**
     * If true, panel will open
     */
    isOpen: boolean;

    /**
     * Contains validation error messages for permission
     */
    validationErrorBag: { [key: string]: string };

    /**
     * Tracks if permission is getting saved to server
     */
    isSavingChanges: boolean;

    /**
     * While saving, captures errors if they occur
     */
    error: Error;
}

// get identities in grid, so we don't add already existing identites
export class AddUsersOrGroupsPanel extends React.Component<IAddUsersOrGroupsPanelProps, {}> {
    private _webPageDataService: HubWebPageDataService;

    constructor(props: IAddUsersOrGroupsPanelProps) {
        super(props);

        this._webPageDataService = this.props.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
        this._resetLocals();
    }

    public render(): JSX.Element {
        const errorMessage: string = this.getErrorMessage();
        return (
            <Panel
                className="add-user-or-group-panel"
                isOpen={this.props.isOpen}
                onDismiss={this._onDismiss}
                type={PanelType.medium}
                isFooterAtBottom={true}
                onRenderFooterContent={this.onRenderFooterContent}
                headerText={PackageResources.FeedSettings_AddPermission_Title}
                closeButtonAriaLabel={PackageResources.AriaLabel_ClosePanel}
                hasCloseButton={this.props.isSavingChanges === false}
            >
                {errorMessage != null && <MessageBar severity={MessageBarSeverity.Error}>{errorMessage}</MessageBar>}
                <IdentityPickerWrapper
                    pageContext={this.props.pageContext}
                    onIdentitiesResolved={this._onIdentitiesResolved}
                    multiIdentitySearch={true}
                />
                <RadioButtonGroup
                    key="addFeedPermissionRole"
                    selectedButtonId={this._feedRole.toString()}
                    text={PackageResources.FeedSettings_AddPermission_RoleDropdown_Label}
                    onSelect={this._onRoleChanged}
                >
                    {this.getChoiceGroupOptions()}
                </RadioButtonGroup>
                {this.props.isSavingChanges && (
                    <Overlay className="feed-overlay">
                        <div className="content">
                            <Spinner
                                size={SpinnerSize.small}
                                label={PackageResources.FeedSettings_Overlay_SavingChanges}
                            />
                        </div>
                    </Overlay>
                )}
            </Panel>
        );
    }

    private getChoiceGroupOptions = (): JSX.Element[] => {
        const options: JSX.Element[] = [
            <RadioButton
                key={FeedRole.Administrator.toString()}
                id={FeedRole.Administrator.toString()}
                text={RoleHelper.roleToLocaleString(FeedRole.Administrator)}
            />,
            <RadioButton
                key={FeedRole.Contributor.toString()}
                id={FeedRole.Contributor.toString()}
                text={RoleHelper.roleToLocaleString(FeedRole.Contributor)}
            />,
            <RadioButton
                key={FeedRole.Collaborator.toString()}
                id={FeedRole.Collaborator.toString()}
                text={RoleHelper.roleToLocaleString(FeedRole.Collaborator)}
            />,
            <RadioButton
                key={FeedRole.Reader.toString()}
                id={FeedRole.Reader.toString()}
                text={RoleHelper.roleToLocaleString(FeedRole.Reader)}
            />
        ];

        return options;
    };

    private onRenderFooterContent = (): JSX.Element => {
        return (
            <div className="footer">
                <PrimaryButton
                    disabled={this.isSaveButtonDisabled()}
                    onClick={this._addPermission}
                    text={PackageResources.FeedSettings_SaveButton_Label}
                />
                <DefaultButton
                    disabled={this.props.isSavingChanges === true}
                    onClick={this._onDismiss}
                    text={PackageResources.FeedSettings_CloseButton_Label}
                />
            </div>
        );
    };

    private isSaveButtonDisabled = (): boolean => {
        return (
            this._identities == null ||
            this._identities.length === 0 ||
            this.props.validationErrorBag[FeedSettingsComponents.permission] != null ||
            // CTA button is blue by default, change it's color to grey while saving
            this.props.isSavingChanges
        );
    };

    private _onIdentitiesResolved = (identities: IIdentity[]): void => {
        this._identities = identities;

        const feedPermissions: FeedPermission[] = this.getFeedPermissions();
        FeedSettingsActionCreator.addUsersOrGroupsPanelContentChange.invoke(feedPermissions);
    };

    private _onRoleChanged = (selectedButtonId: string): void => {
        this._feedRole = parseInt(selectedButtonId, 10) as FeedRole;

        const feedPermissions: FeedPermission[] = this.getFeedPermissions();
        FeedSettingsActionCreator.addUsersOrGroupsPanelContentChange.invoke(feedPermissions);
    };

    private _addPermission = (): void => {
        const feedPermissions: FeedPermission[] = this.getFeedPermissions();

        FeedSettingsActionCreator.savePermissionsClicked.invoke(feedPermissions);
    };

    private _onDismiss = (): void => {
        this._resetLocals();

        FeedSettingsActionCreator.toggleAddUserOrGroupPanelDisplay.invoke(false /*close panel*/);
    };

    private getErrorMessage(): string {
        const validationErrorMessage: string = this.props.validationErrorBag[FeedSettingsComponents.permission];

        if (validationErrorMessage != null) {
            return validationErrorMessage;
        }

        if (this.props.error != null) {
            return this.props.error.message;
        }

        return null;
    }

    private getFeedPermissions(): FeedPermission[] {
        if (this._identities == null || this._identities.length === 0) {
            return null;
        }

        const feedPermissions: FeedPermission[] = [];
        this._identities.forEach((identity: IIdentity) => {
            const feedPermission: FeedPermission = {
                displayName: identity.displayName,
                identityId: identity.localId,
                role: this._feedRole,
                identityDescriptor: null,
                isInheritedRole: false
            };
            feedPermissions.push(feedPermission);
        });

        return feedPermissions;
    }

    private _resetLocals(): void {
        this._identities = null;
        this._feedRole = FeedRole.Reader;
    }

    private _identities: IIdentity[];
    private _feedRole: FeedRole;
}
